import React, { useEffect } from "react";
import { Handle, Node, NodeProps, Position, useUpdateNodeInternals } from "@xyflow/react";
import { Phone, Trash2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useFeature } from "@/hooks/useFeature";
import { ActionNodeData } from "@/model/interface/CallFlow";
import { edgeKeysFor, missingRequiredFields } from "@/util/callFlowNodes";
import { useFlowBuilder } from "../components/FlowBuilderContext";
import { PICKABLE_ACTION_TYPES, actionTypeOption } from "./actionTypeCatalog";
import ActionNodeForm from "./ActionNodeForm";
import BranchHandles from "./BranchHandles";

export type ActionNodeType = Node<ActionNodeData>;

export default function ActionNode({ id, data, selected }: NodeProps<ActionNodeType>) {
  const updateNodeInternals = useUpdateNodeInternals();
  const { orientation, deleteNode, chooseActionType } = useFlowBuilder();
  const hasRoutingSteps = useFeature("CALL_ROUTING_STEPS");
  const option = actionTypeOption(data.actionType);
  const Icon = option?.icon ?? Phone;
  const edgeKeys = edgeKeysFor(data.actionType, data);
  const missingFields = missingRequiredFields(data.actionType, data);
  const isHorizontal = orientation === "horizontal";
  const handleLayoutKey = `${orientation}:${edgeKeys.join("|")}`;

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleLayoutKey, updateNodeInternals]);

  return (
    <Card className={cn("nopan relative w-[340px] shadow-none", selected && "border-primary")}>
      {!isHorizontal && <Handle type="target" position={Position.Top} />}

      <div className="relative flex items-center justify-between gap-2 border-b px-4 py-3">
        {isHorizontal && <Handle type="target" position={Position.Left} />}
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </div>
          <div className="truncate text-base font-semibold">{option?.label ?? data.label}</div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs">
                Change
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {PICKABLE_ACTION_TYPES.filter((candidate) => candidate.actionType !== data.actionType).map(
                (candidate) => {
                  const CandidateIcon = candidate.icon;
                  const isLocked = candidate.isPro && !hasRoutingSteps;
                  return (
                    <DropdownMenuItem
                      key={candidate.actionType}
                      disabled={isLocked}
                      onClick={() => chooseActionType(id, candidate.actionType)}
                    >
                      <CandidateIcon className="mr-2 h-4 w-4" />
                      {candidate.label}
                      {isLocked && (
                        <span className="ml-auto pl-3 text-[10px] font-semibold uppercase tracking-wider">Pro</span>
                      )}
                    </DropdownMenuItem>
                  );
                },
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Separator orientation="vertical" className="h-4" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            title="Remove this step"
            onClick={() => deleteNode(id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {missingFields.length > 0 && (
          <span className="status-pill-warning">
            <TriangleAlert className="h-3 w-3" />
            Missing: {missingFields.join(", ")}
          </span>
        )}
        <ActionNodeForm nodeId={id} data={data} />
      </div>

      <BranchHandles nodeId={id} edgeKeys={edgeKeys} />
    </Card>
  );
}
